// Definições (IDEA-044).
//
// A app nunca teve uma página destas — e é também por isso que as notificações
// não tinham onde morar. Nasce com o mínimo que justifica existir: as
// notificações deste aparelho, e a conta.
//
// "Deste aparelho" não é um detalhe de escrita. Uma inscrição push pertence ao
// browser onde foi feita, não à conta: ligar no telemóvel não liga no portátil,
// e a página tem de o dizer, senão a primeira vez que não chegar nada ao outro
// aparelho parece uma avaria.

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keys, usePushKey } from "@/api/queries";
import { useSession } from "@/api/session";
import { Consulta } from "@/components/ui/EstadoConsulta";
import { useTitle } from "@/components/ui/useTitle";
import { desligar, estadoActual, ligar, type EstadoPush } from "@/lib/push";

export function Definicoes() {
  useTitle("Definições");

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
      <header className="mb-6 text-center">
        <h1 className="font-titulo text-3xl text-tinta-800 sm:text-4xl">Definições</h1>
        <p className="mt-1 text-sm text-tinta-600">O que é teu e o que é deste aparelho.</p>
      </header>

      <Notificacoes />
      <Conta />
    </main>
  );
}

function Seccao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded-2xl border border-ouro/20 bg-creme-50/80 p-5">
      <h2 className="mb-3 font-titulo text-xl text-tinta-800">{titulo}</h2>
      {children}
    </section>
  );
}

function Notificacoes() {
  const query = usePushKey();
  const queryClient = useQueryClient();
  const [estado, setEstado] = useState<EstadoPush | null>(null);
  const [aMudar, setAMudar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const subscritoNoServidor = query.data?.subscrito ?? false;

  // O estado real só se sabe perguntando ao browser, o que é assíncrono e não
  // cabe num render. Recalcula-se quando a resposta da API chega, porque uma
  // das três perguntas ("o servidor tem-me?") é dela.
  useEffect(() => {
    if (!query.data) return;
    let vivo = true;
    void estadoActual(subscritoNoServidor).then((e) => {
      if (vivo) setEstado(e);
    });
    return () => {
      vivo = false;
    };
  }, [query.data, subscritoNoServidor]);

  async function alternar() {
    const chave = query.data?.chavePublica;
    if (!chave) return;

    setAMudar(true);
    setErro(null);
    try {
      setEstado(estado === "ligado" ? await desligar() : await ligar(chave));
      // O servidor passou a saber outra coisa sobre este aparelho.
      await queryClient.invalidateQueries({ queryKey: keys.pushKey });
    } catch (e) {
      setErro((e as Error).message || "Não foi possível mudar as notificações.");
    } finally {
      setAMudar(false);
    }
  }

  return (
    <Seccao titulo="Notificações">
      <Consulta query={query} linhas={1}>
        {(dados) => {
          // O servidor não tem chaves VAPID: não há push nenhuma para ligar, e
          // um interruptor aqui seria uma promessa que ninguém pode cumprir.
          if (!dados.chavePublica) {
            return (
              <p className="text-sm text-tinta-600">
                As notificações push não estão configuradas no servidor. Falta o par de chaves
                VAPID.
              </p>
            );
          }

          if (estado === null) {
            return <p className="text-sm text-tinta-600">A verificar…</p>;
          }

          if (estado === "sem-suporte") {
            return (
              <p className="text-sm text-tinta-600">
                Este browser não sabe receber notificações. No telemóvel, instala a app no ecrã
                inicial e tenta por lá.
              </p>
            );
          }

          if (estado === "negado") {
            return (
              <p className="text-sm text-tinta-600">
                As notificações foram bloqueadas para este site. O browser já não volta a
                perguntar — tens de as reactivar nas definições dele, no cadeado ao lado do
                endereço.
              </p>
            );
          }

          return (
            <>
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-tinta-700">
                  {estado === "ligado"
                    ? "Este aparelho avisa-te quando for a tua vez de jogar."
                    : "Ligar para saberes que há uma ronda à tua espera sem abrir a app."}
                </p>
                <button
                  type="button"
                  onClick={() => void alternar()}
                  disabled={aMudar}
                  aria-pressed={estado === "ligado"}
                  className={`shrink-0 rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 disabled:opacity-60 ${
                    estado === "ligado"
                      ? "border border-ouro/40 text-tinta-700 hover:bg-creme-200/70"
                      : "bg-ouro text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] hover:-translate-y-0.5 hover:bg-ouro-600"
                  }`}
                >
                  {aMudar ? "…" : estado === "ligado" ? "Desligar" : "Ligar"}
                </button>
              </div>
              <p className="mt-2 text-xs text-tinta-500">
                Vale só para este aparelho — cada telemóvel ou computador liga-se por si.
              </p>
              {erro && <p className="mt-2 text-xs text-red-700">{erro}</p>}
            </>
          );
        }}
      </Consulta>
    </Seccao>
  );
}

function Conta() {
  const { user } = useSession();

  return (
    <Seccao titulo="Conta">
      <p className="text-sm text-tinta-700">
        Com a sessão aberta como{" "}
        <span className="font-semibold text-tinta-800">{user?.name ?? user?.email}</span>.
      </p>
      <p className="mt-1 text-xs text-tinta-500">Para sair, usa o menu no canto superior direito.</p>
    </Seccao>
  );
}
